import React, { useState, useEffect } from "react";
import CustomSelect from "@components/common/CustomSelect";
import { XCircle } from "lucide-react";
import { CustomSelectItemProps } from "@components/common/CommonTypes";
import { capitalizeWords } from "@utils/utils";
import { UsecaseContext } from "@services/providers/UsecaseProvider";
import { Input } from "@components/ui/input";

interface AnomalyDefeatItemProps {
  className?: string;
  data: any;
  type: string;
  label: string;
}

const AnomalyMeta: React.FC<AnomalyDefeatItemProps> = ({
  className = "",
  data,
  label = "IF",
  type = "includeMeta",
}) => {
  const [items, setItems] = useState<CustomSelectItemProps[]>([]);
  const [inputValue, setInputValue] = useState("");
  const {
    DefeatData,
    updateDefeatData,
    CurrentView,
    metaMapping,
    AnomalyData,
    allEvents,
    FieldData,
    usecaseData,
  } = React.useContext(UsecaseContext);

  useEffect(() => {
    setInputValue("");
  }, [data?.metaId, data?.condition]);

  // handle enter key
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();

      const value = inputValue.trim();

      if (!value) return;

      const updatedValues = [...(data.values || []), value];

      changeValue(updatedValues);

      setInputValue("");
    }
  }

  const eventRecord = JSON.parse((allEvents || {}).data || "[]");

  let user_or_entity = AnomalyData?.usecase_log_mapping?.usecase_type || "";
  let log_source = AnomalyData?.log_source || "";

  if (user_or_entity == "") {
    const eventList = AnomalyData.event_list || [];
    if (Object.keys(eventList).length > 0) {
      const event = eventList[Object.keys(eventList)[0]];
      const match = eventRecord.find((item: any) => item.event_id == event);
      if (match) {
        user_or_entity = match.event_log_mapping?.event_type || "";
        if (
          "block_list" in match &&
          match.block_list.length > 0 &&
          "log_source" in match.block_list[0]
        ) {
          log_source = match?.block_list?.[0]?.log_source?.value || "";
        } else if (
          "block_list" in match &&
          match.block_list.length > 0 &&
          "group_list" in match.block_list[0] &&
          match.block_list[0].group_list.length > 0 &&
          "log_source" in match.block_list[0].group_list[0]
        ) {
          log_source =
            match?.block_list?.[0]?.group_list[0]?.log_source?.value || "";
        }
      }
    }
  }

  if (user_or_entity.trim() == "") {
    user_or_entity = usecaseData?.event_log_mapping?.event_type || "";
  }

  const current_meta_data_temp =
    type == "includeMeta" ? DefeatData.includeMeta : DefeatData.excludeMeta;
  const current_meta_data = current_meta_data_temp.filter(
    (m: any) => m.metaId != data.metaId,
  );

  const categoryDropdown: CustomSelectItemProps[] = [];
  let currentMetaMapping: any = [];
  if (user_or_entity && user_or_entity in metaMapping) {
    const tempData = metaMapping[user_or_entity] || [];
    currentMetaMapping = tempData;
    tempData.forEach((element: any) => {
      const exists = current_meta_data.filter(
        (m: any) => element.name == m.condition,
      );
      // if (element.usecase && exists.length == 0) {
      if (
        element.usecase &&
        exists.length == 0 // &&
        // "mapping" in element &&
        // Object.keys(element.mapping).length > 0
      ) {
        // Only show fields which have mapping defined (till 4.7.4)
        categoryDropdown.push({
          label: element.display_name || capitalizeWords(element.name),
          value: element.name,
        });
      }
    });
  }

  function changeMetaLogSource(source: string) {
    if ("data" in FieldData) {
      const standards = FieldData.data["standard"];
      const tempFields: any = [];
      const logs = FieldData.data["logs"];
      const ls = source;
      if (ls in logs) {
        const fields =
          logs[ls]?.data?.length > 0 ? logs[ls].data : logs[ls].fields;
        for (const field of fields) {
          if (field.usecaseField === 1 || field.for_rule_builder === 1) {
            if (
              FieldData.data.seen_by_log[ls] &&
              FieldData.data.seen_by_log[ls][field.field]
            ) {
              standards[FieldData.data.seen_by_log[ls][field.field]]["name"] =
                field.field;
              tempFields.push(
                standards[FieldData.data.seen_by_log[ls][field.field]],
              );
            } else if (FieldData.data.seen[field.field] in standards) {
              standards[FieldData.data.seen[field.field]]["name"] = field.field;
              tempFields.push(standards[FieldData.data.seen[field.field]]);
            } else if (field.field in standards) {
              standards[field.field]["name"] = field.field;
              tempFields.push(standards[field.field]);
            }
          }
        }
      }
      const itm = tempFields.map((tmp: any) => ({
        label: tmp.display_name || tmp.name,
        value: tmp.name,
      }));
      return itm;
    }
  }

  function loadValues(e: string) {
    const matched = currentMetaMapping.find((item: any) => item.name == e);
    const itemsData: CustomSelectItemProps[] = [];
    const mapping = matched?.mapping || {};
    if (Object.keys(mapping).length > 0) {
      Object.keys(mapping).forEach((key: string) => {
        itemsData.push({ value: mapping[key].toString(), label: key });
      });
    } else {
      const mp = changeMetaLogSource(log_source);
      for (const m of mp) {
        itemsData.push({ value: m.value.toString(), label: m.label });
      }
    }
    setItems(itemsData);
  }

  function changeCategory(e: any) {
    loadValues(e);
    const defeat = DefeatData[type].find(
      (item: any) => item.metaId == data.metaId,
    );
    if (defeat) {
      defeat["type"] = "fromList";
      defeat["condition"] = e;
      defeat["values"] = [];
      updateDefeatData({ ...DefeatData });
    }
  }

  const fieldDataType = metaMapping[user_or_entity]?.find(
    (item: any) => item.name == data.condition,
  )?.valueType;

  function changeValue(e: any) {
    const defeat = DefeatData[type].find(
      (item: any) => item.metaId == data.metaId,
    );
    if (defeat) {
      // defeat["values"] = e?.map((val: any) => parseInt(val));
      defeat["values"] =
        fieldDataType == "Boolean"
          ? [e.toString()]
          : e?.map((val: any) => val.toString());
      updateDefeatData({ ...DefeatData });
    }
  }

  function removeItem() {
    DefeatData[type] = DefeatData[type].filter(
      (item: any) => item.metaId != data.metaId,
    );
    updateDefeatData({ ...DefeatData });
  }

  useEffect(() => {
    if (data) {
      const condition = data.condition;
      loadValues(condition);
    }
  }, [data]);

  return (
    <>
      <div
        className={
          "w-full grid grid-cols-12 gap-2 border-b pb-2 !items-center " +
          className
        }
      >
        <div className="col-span-1 text-sm flex justify-end me-2 !items-center">
          {label}
        </div>
        <div className="col-span-4 p-5 m-2">
          <CustomSelect
            data={categoryDropdown}
            defaultValue={data.condition}
            placeholder="Category..."
            onSelect={(e: any) => changeCategory(e)}
          />
        </div>
        <div className="col-span-1 relative">
          <div className="absolute top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4 text-sm">
            {fieldDataType == "Boolean" ? "IS" : "IN"}
          </div>
        </div>
        <div className="col-span-4">
          {(fieldDataType && fieldDataType == "Boolean") ||
          data.condition == "Integer_Attribute_preselected" ||
          data.condition == "float_preselected" ? (
            <CustomSelect
              data={items}
              placeholder="Values..."
              defaultValue={
                fieldDataType == "Boolean"
                  ? (data.values || "").toString()
                  : (data.values || []).map((val: string) => val.toString())
              }
              multiSelect={
                (CurrentView == "anomaly" || CurrentView == "event") &&
                fieldDataType != "Boolean"
              }
              onSelect={(e: any) => changeValue(e)}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-2 border border-input rounded-md p-2">
              {(data.values || []).map((item: string, index: number) => (
                <div
                  key={index}
                  className="flex items-center gap-1 bg-primary text-white px-2 py-1 rounded-md text-sm"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => {
                      const updated = data.values.filter(
                        (_: string, i: number) => i !== index,
                      );
                      changeValue(updated);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}

              <Input
                type="text"
                value={inputValue}
                placeholder="Enter values with enter key"
                className="flex-1 bg-transparent outline-none text-sm min-w-[150px]"
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
          )}
        </div>
        <div className="col-span-1 my-auto">
          <XCircle
            size={20}
            className="cursor-pointer ms-2 text-primary"
            onClick={() => removeItem()}
          />
        </div>
      </div>
    </>
  );
};

export default AnomalyMeta;
